import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Search,
  Menu,
  X,
  ChevronDown,
} from "lucide-react";

import { navigation, footerInfo } from "../mock";

const LOGO = "/assets/images/logo.png";

const Header = () => {
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);

  // ================= KEEP ORIGINAL NAVIGATION + INSERT RECRUITMENT =================
  const updatedNavigation = [];

  navigation.forEach((item) => {
    updatedNavigation.push(item);

    if (item.label === "Programs") {
      updatedNavigation.push({
        label: "Recruitment",
        path: "/recruitment",
      });
    }
  });

  return (
    <>
      <header className="bg-white relative z-50">

        {/* ================= TOP BAR ================= */}
        <div className="bg-[#082567] text-white text-xs hidden md:block">

          <div className="max-w-[1400px] mx-auto px-6 py-2 flex items-center justify-between">

            <div className="flex items-center gap-8">

              <span className="flex items-center gap-2">
                <Mail size={13} />
                {footerInfo.emails[0]}
              </span>

              <span className="flex items-center gap-2">
                <Phone size={13} />
                {footerInfo.phones[1]}
              </span>

              <span className="flex items-center gap-2">
                <MapPin size={13} />
                Wellington Bassey Way, Uyo, Akwa Ibom State, Nigeria
              </span>
            </div>

            <div className="flex items-center gap-4">
              <Facebook size={14} />
              <Twitter size={14} />
              <Linkedin size={14} />
              <Instagram size={14} />
            </div>
          </div>
        </div>

        {/* ================= LOGO SECTION ================= */}
        <div className="bg-white">

          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6 flex items-center justify-between">

            {/* LOGO */}
            <Link
              to="/"
              className="flex items-center gap-3 min-w-0"
            >
              <img
                src={LOGO}
                alt="AKSUBEB"
                className="
                  w-[62px]
                  h-[62px]
                  md:w-[95px]
                  md:h-[95px]
                  object-contain
                  flex-shrink-0
                "
              />

              <h1
                className="
                  text-[34px]
                  sm:text-[42px]
                  md:text-[78px]
                  leading-none
                  font-black
                  tracking-[-2px]
                  md:tracking-[-4px]
                  text-black
                  truncate
                "
                style={{
                  fontFamily: "'Arial Black', sans-serif",
                }}
              >
                AKSUBEB
              </h1>
            </Link>

            {/* MOBILE BUTTON */}
            <button
              onClick={() => setMobileOpen(true)}
              className="
                lg:hidden
                w-12
                h-12
                flex
                items-center
                justify-center
                rounded-xl
                border
                border-slate-200
                flex-shrink-0
              "
            >
              <Menu size={28} />
            </button>
          </div>
        </div>

        {/* ================= DESKTOP STICKY NAVBAR ================= */}
        <div className="hidden lg:block sticky top-0 z-[999] border-t border-slate-100 border-b border-slate-100 bg-white shadow-sm">

          <div className="max-w-[1400px] mx-auto px-6">

            <div className="h-[78px] flex items-center justify-center relative">

              <nav className="flex items-center gap-14">

                {updatedNavigation.map((item) => {
                  const isActive =
                    location.pathname === item.path;

                  const hasChildren =
                    item.children &&
                    item.children.length > 0;

                  return (
                    <div
                      key={item.label}
                      className="relative group"
                    >
                      <Link
                        to={item.path}
                        className={`flex items-center gap-1 text-[20px] font-semibold transition-colors ${
                          isActive
                            ? "text-[#1d4ed8]"
                            : "text-black hover:text-[#1d4ed8]"
                        }`}
                      >
                        {item.label}

                        {hasChildren && (
                          <span className="font-bold text-[18px]">
                            +
                          </span>
                        )}
                      </Link>

                      {/* DROPDOWN */}
                      {hasChildren && (
                        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">

                          <div className="bg-[#f5f5f5] min-w-[320px] shadow-xl rounded-sm py-5">

                            {item.children.map((c) => (
                              <Link
                                key={c.label}
                                to={c.path}
                                className="block px-10 py-3 text-[17px] text-[#222] hover:text-[#1d4ed8]"
                              >
                                {c.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>

              {/* SEARCH */}
              <button className="absolute right-0 text-black hover:text-[#1d4ed8]">
                <Search size={22} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ================= MOBILE SIDEBAR OVERLAY ================= */}
      <div
        className={`
          fixed
          inset-0
          bg-black/50
          z-[9998]
          transition-opacity
          duration-300
          lg:hidden
          ${
            mobileOpen
              ? "opacity-100 visible"
              : "opacity-0 invisible"
          }
        `}
        onClick={() => setMobileOpen(false)}
      />

      {/* ================= MOBILE SIDEBAR ================= */}
      <div
        className={`
          fixed
          top-0
          left-0
          h-full
          w-[320px]
          max-w-[85vw]
          bg-white
          z-[9999]
          shadow-2xl
          transition-transform
          duration-300
          overflow-y-auto
          lg:hidden
          ${
            mobileOpen
              ? "translate-x-0"
              : "-translate-x-full"
          }
        `}
      >
        {/* SIDEBAR HEADER */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-slate-200">

          <div className="flex items-center gap-3">

            <img
              src={LOGO}
              alt="AKSUBEB"
              className="w-[52px] h-[52px] object-contain"
            />

            <h2
              className="text-[28px] font-black tracking-[-2px]"
              style={{
                fontFamily: "'Arial Black', sans-serif",
              }}
            >
              AKSUBEB
            </h2>
          </div>

          <button
            onClick={() => setMobileOpen(false)}
            className="
              w-10
              h-10
              rounded-lg
              border
              border-slate-200
              flex
              items-center
              justify-center
            "
          >
            <X size={24} />
          </button>
        </div>

        {/* NAVIGATION */}
        <nav className="px-5 py-5">

          {updatedNavigation.map((item) => (
            <div
              key={item.label}
              className="border-b border-slate-100"
            >
              <Link
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className="
                  flex
                  items-center
                  justify-between
                  py-4
                  text-[17px]
                  font-semibold
                  text-slate-800
                "
              >
                <span>{item.label}</span>

                {item.children?.length > 0 && (
                  <ChevronDown size={18} />
                )}
              </Link>

              {/* CHILDREN */}
              {item.children?.length > 0 && (
                <div className="pb-4 pl-4">

                  {item.children.map((c) => (
                    <Link
                      key={c.label}
                      to={c.path}
                      onClick={() => setMobileOpen(false)}
                      className="
                        block
                        py-2
                        text-[15px]
                        text-slate-600
                      "
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
};

export default Header;